import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Loader2, MailCheck, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, errMsg } from "@/lib/api";
import { Brand } from "@/components/AppLayout";

function Shell({ children, testId }) {
  return (
    <div className="min-h-screen grid place-items-center p-6">
      <div className="w-full max-w-md rounded-2xl border bg-white p-8 sm:p-10 fade-up" data-testid={testId}>
        <div className="mb-8"><Brand /></div>
        {children}
      </div>
    </div>
  );
}

export function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await api.post("/auth/forgot-password", { email }); setSent(true); }
    catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <Shell testId="forgot-password-page">
      {sent ? (
        <div className="text-center" data-testid="forgot-password-sent">
          <MailCheck className="h-10 w-10 mx-auto text-primary" />
          <h1 className="mt-4 text-2xl font-bold">Check your email</h1>
          <p className="mt-2 text-sm text-slate-500">If an account exists for <b>{email}</b>, we've sent a link to reset your password. It expires in 1 hour. Check your spam folder if you don't see it.</p>
          <Button asChild variant="outline" className="mt-8 rounded-full"><Link to="/login" data-testid="forgot-back-to-login">Back to sign in</Link></Button>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <h1 className="text-2xl font-bold">Forgot your password?</h1>
          <p className="text-sm text-slate-500">Enter your account email and we'll send you a secure link to set a new one.</p>
          <div className="space-y-1.5">
            <Label htmlFor="fp-email">Email</Label>
            <Input id="fp-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="h-11" data-testid="forgot-email-input" />
          </div>
          <Button type="submit" className="w-full h-11 rounded-full" disabled={busy} data-testid="forgot-submit-button">
            {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Send reset link
          </Button>
          <Link to="/login" className="block text-center text-sm text-slate-500 hover:text-primary" data-testid="forgot-cancel-link">Back to sign in</Link>
        </form>
      )}
    </Shell>
  );
}

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [f, setF] = useState({ password: "", confirm: "" });
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (f.password.length < 8) return toast.error("Password must be at least 8 characters");
    if (f.password !== f.confirm) return toast.error("Passwords don't match");
    setBusy(true);
    try { await api.post("/auth/reset-password", { token, new_password: f.password }); setDone(true); }
    catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  if (!token)
    return (
      <Shell testId="reset-password-page">
        <p className="text-sm text-red-600" data-testid="reset-missing-token">This reset link is incomplete. Please request a new one.</p>
        <Button asChild className="mt-6 rounded-full"><Link to="/forgot-password">Request new link</Link></Button>
      </Shell>
    );
  return (
    <Shell testId="reset-password-page">
      {done ? (
        <div className="text-center" data-testid="reset-password-done">
          <CheckCircle2 className="h-10 w-10 mx-auto text-emerald-600" />
          <h1 className="mt-4 text-2xl font-bold">Password updated</h1>
          <p className="mt-2 text-sm text-slate-500">You can now sign in with your new password.</p>
          <Button className="mt-8 rounded-full px-8" onClick={() => navigate("/login")} data-testid="reset-go-login-button">Sign in</Button>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <h1 className="text-2xl font-bold">Choose a new password</h1>
          <p className="text-sm text-slate-500">Use at least 8 characters.</p>
          <div className="space-y-1.5">
            <Label htmlFor="rp-pass">New password</Label>
            <Input id="rp-pass" type="password" autoComplete="new-password" required value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} className="h-11" data-testid="reset-password-input" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="rp-confirm">Confirm new password</Label>
            <Input id="rp-confirm" type="password" autoComplete="new-password" required value={f.confirm} onChange={(e) => setF({ ...f, confirm: e.target.value })} className="h-11" data-testid="reset-confirm-input" />
          </div>
          <Button type="submit" className="w-full h-11 rounded-full" disabled={busy} data-testid="reset-submit-button">
            {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Update password
          </Button>
        </form>
      )}
    </Shell>
  );
}
